// RozpisLegacy.jsx — KARTA ZÁPISU ROZPISU (WorkScheduleModal) + poznámky zákazky, vyrezané 1:1 zo starého views/RozpisView.jsx (V2).
// rozpis_v3 (29. 9. 2026): nová obrazovka Rozpisu používa túto kartu bez zmeny — kód sa nepíše druhýkrát (ústava 0.J).
// Obsahuje: výber zákazky/budúcej zákazky, obdobie, stav, poznámku (116), kontakt na zákazníka (103.H), väzby dopyt/CP (103.I), rekapituláciu pred uložením (12.0/3).
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { KontaktnaOsoba } from '../components/KontaktyPartnera.jsx'
import TelefonInput from '../components/TelefonInput.jsx'
import { fetchAuth, fetchAuthJSON } from '../auth/fetchAuth.js'
import { normPhone } from '../utils/phone.js'
import { Btn, BtnAdd, BtnGhost, BtnNav, BtnDanger } from '../components/Btn.jsx'
import { Input, Textarea } from '../components/Input.jsx'
import { Modal } from '../components/Modal.jsx'
import { naPracovneDni } from '../components/rozpis/rozpisUtil.js'
import { useToast } from '../components/Toast.jsx'
import { useRecordEditor } from '../hooks/useRecordEditor.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { SearchableSelect } from '../components/Ui.jsx'
import { COLORS, TYPO, SPACE, RADIUS, TINT, tint, hladaj } from '../theme.js'
import { addWorkDays, nextWorkDay, prevWorkDay, workDaysBetween, fmtDM } from '../utils/workdays.js'
import { sviatok as skHoliday } from '../utils/sviatky.js'

const STATUS_CFG = {
  planned: { label: 'Dohodnuté', color: '#2563eb' },
  ongoing: { label: 'Prebieha', color: '#f59e0b' },
  done: { label: 'Ukončené', color: '#16a34a' },
  vacation: { label: 'Dovolenka', color: '#cbd5e1', textDark: true },
}
const STATUS_ALIAS = { completed: 'done' }
const statusCfg = (s) => STATUS_CFG[STATUS_ALIAS[s] || s] || STATUS_CFG.planned
const STATUS_OPTS = [
  { value: 'planned', label: 'Dohodnuté' },
  { value: 'ongoing', label: 'Prebieha' },
  { value: 'done', label: 'Ukončené' },
  { value: 'vacation', label: 'Dovolenka' },
]
function toLocalISO(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') }
function parseLocalDate(str) { const [y, m, d] = String(str).slice(0, 10).split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1) }
function mondayOf(dateStr) { const d = parseLocalDate(dateStr); const w = (d.getDay() + 6) % 7; d.setDate(d.getDate() - w); return toLocalISO(d) }
function sundayOf(dateStr) { const d = parseLocalDate(mondayOf(dateStr)); d.setDate(d.getDate() + 6); return toLocalISO(d) }

function PoznamkaBuducaZakazka({ ed, v }) {
  return (
    <div style={{ padding: SPACE.sm, background: 'rgba(245,158,11,0.06)', border: `1px solid ${COLORS.total || '#fbbf24'}`, borderRadius: RADIUS.md, marginBottom: SPACE.sm }}>
      <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
        Poznámka k budúcej zákazke
      </div>
      <textarea value={v.notes || ''} onChange={e => ed.set('notes', e.target.value)} rows={3}
        placeholder="Výmery, dohodnuté ceny, kontakt na stavbu — všetko, čo bude platiť pre zákazku."
        style={{ width: '100%', padding: '7px 9px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box', resize: 'vertical' }} />
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 3 }}>
        Zápis ešte nemá zákazku. Keď ju priradíš, tento text patrí k nej a prežije každé prehodenie partie.
      </div>
    </div>
  )
}

function PoznamkaZakazky({ projectId, projects, ed, v }) {
  const toast = useToast()   // chybalo — pri chybe ulozenia padalo (30.7.)
  const prj = (projects || []).find(p => p.id === projectId)
  const [txt, setTxt] = useState('')
  const [ulozene, setUlozene] = useState('')
  const [stav, setStav] = useState('')
  useEffect(() => { const n = String((prj && prj.notes) || ''); setTxt(n); setUlozene(n); setStav('') }, [projectId, prj && prj.notes])
  // bez zakazky: v okne zapisu sa da pisat (mame `ed`), v panely komunikacie len vysvetlenie
  if (!projectId) return ed
    ? <PoznamkaBuducaZakazka ed={ed} v={v} />
    : (
      <div style={{ padding: SPACE.sm, background: 'rgba(245,158,11,0.06)', border: `1px dashed ${COLORS.total || '#fbbf24'}`, borderRadius: RADIUS.md, marginBottom: SPACE.sm, fontSize: TYPO.sizes.xs, color: COLORS.text }}>
        <strong>Zápis ešte nemá zákazku.</strong> Poznámku k budúcej zákazke napíšeš v okne zápisu (dvojklik na blok).
      </div>
    )
  const uloz = async () => {
    if (txt === ulozene) return
    setStav('ukladám…')
    try {
      const r = await fetchAuth('/api/projects/' + encodeURIComponent(projectId) + '/notes', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes: txt }),
      })
      if (!r.ok) throw new Error('uloženie zlyhalo')
      if (prj) prj.notes = txt
      setUlozene(txt); setStav('uložené')
      setTimeout(() => setStav(''), 2500)
    } catch (e) { setStav(''); toast.error('Poznámku zákazky sa nepodarilo uložiť: ' + e.message) }
  }
  return (
    <div style={{ padding: SPACE.sm, background: 'rgba(37,99,235,0.04)', border: `1px solid ${COLORS.brand}`, borderRadius: RADIUS.md, marginBottom: SPACE.sm }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: COLORS.brand, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Poznámka zákazky {prj && prj.name ? '· ' + prj.name : ''}
        </span>
        <span style={{ fontSize: TYPO.sizes.xs, color: stav === 'uložené' ? COLORS.add : COLORS.muted }}>{stav}</span>
      </div>
      <textarea value={txt} onChange={e => setTxt(e.target.value)} onBlur={uloz} rows={3}
        placeholder="Výmery, dohodnuté ceny, kontakt na stavbu — všetko, čo platí pre zákazku bez ohľadu na partiu."
        style={{ width: '100%', padding: '7px 9px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box', resize: 'vertical' }} />
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 3 }}>
        Platí pre celú zákazku a všetky partie. Keď sa práca zajtra prehodí inej partii, toto zostáva.
      </div>
    </div>
  )
}

export function WorkScheduleModal({ data, employees, projects, onClose, onSaved, onCreateProject, custInfo, povolVikend }) {
  const toast = useToast()
  const { user } = useAuth()
  const isEdit = !!data.rec
  const rec = data.rec
  const [deleting, setDeleting] = useState(false)

  const empLabel = useCallback((id) => {
    const e = employees.find(x => x.id === id)
    return e ? `${e.firstName} ${e.lastName}` : (id || '—')
  }, [employees])
  const projLabel = useCallback((id) => {
    const p = projects.find(x => x.id === id)
    return p ? `${p.name} (${id})` : (id || '—')
  }, [projects])

  // polia pre DIFF (ústava 53.8) — format prekladá id → čitateľný názov
  const editFields = useMemo(() => ([
    { name: 'employee_id',  label: 'Predák',         format: v => empLabel(v) },
    { name: 'project_id',   label: 'Zákazka',        format: v => v ? projLabel(v) : '—' },
    { name: 'project_name', label: 'Budúca zákazka', format: v => v || '—' },
    { name: 'start_date',   label: 'Od' },
    { name: 'end_date',     label: 'Do' },
    { name: 'status',       label: 'Stav',           format: v => statusCfg(v).label },
    { name: 'notes',        label: 'Poznámka',       format: v => v || '—' },
    { name: 'kontakt_meno',    label: 'Zákazník',  format: v => v || '—' },
    { name: 'kontakt_telefon', label: 'Telefón',   format: v => v || '—' },
    { name: 'kontakt_email',   label: 'E-mail',    format: v => v || '—' },
    { name: 'kontakt_adresa',  label: 'Adresa',    format: v => v || '—' },
  ]), [empLabel, projLabel])

  const ed = useRecordEditor({
    resource: 'work-schedule',
    id: isEdit ? rec.id : null,
    user,
    initial: {
      employee_id:  rec?.employee_id || data.empId || '',
      project_id:   rec?.project_id || '',
      project_name: rec?.project_name || '',
      start_date:   rec?.start_date || data.start_date || '',
      end_date:     rec?.end_date || data.end_date || '',
      status:       rec?.status || 'planned',
      notes:        rec?.notes || '',
      // KONTAKT NA ZAKAZNIKA (Mirko GO 28.7.) — v Rozpise smie byt aj buduca zakazka,
      // vtedy niet odkial kontakt vziat, preto sedi priamo na zapise.
      kontakt_meno:    rec?.kontakt_meno || '',
      kontakt_telefon: rec?.kontakt_telefon || '',
      kontakt_email:   rec?.kontakt_email || '',
      kontakt_adresa:  rec?.kontakt_adresa || '',
    },
    fields: editFields,
    onSubmit: async (values) => {
      const url = isEdit ? `/api/work-schedule/${rec.id}` : '/api/work-schedule'
      const r = await fetchAuth(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      })
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        throw new Error(j.error || `HTTP ${r.status}`)
      }
    },
  })

  const v = ed.values
  const readOnly = !ed.canEdit
  const doClose = () => ed.requestClose(onClose)
  const emp = employees.find(e => e.id === v.employee_id)
  const empName = emp ? `${emp.firstName} ${emp.lastName}` : (v.employee_id || '')

  function validate(vals) {
    if (!vals.employee_id) return 'Chýba predák'
    if (!vals.start_date || !vals.end_date) return 'Vyplň obdobie od–do'
    if (vals.status !== 'vacation' && !vals.project_id && !(vals.project_name || '').trim())
      return 'Vyber zákazku alebo zadaj názov budúcej zákazky'
    return null
  }

  async function handleConfirm() {
    const ok = await ed.submit()
    if (ok) { toast.success(isEdit ? 'Zápis upravený' : 'Zápis pridaný'); onSaved() }
  }

  async function doDelete() {
    // Ústava 12.0.3/617 — kontextové potvrdenie (nie slepý jeden klik) + 603 — vratné cez Späť (Mirko 2026-06-04)
    const _nm = rec.project_name || (rec.project_id ? rec.project_id : 'Zápis')
    const _dt = rec.start_date ? (rec.start_date + (rec.end_date && rec.end_date !== rec.start_date ? ' \u2192 ' + rec.end_date : '')) : ''
    if (!window.confirm('Zmazať zápis z Rozpisu prác?\n\n' + _nm + (_dt ? '\n' + _dt : '') + '\n\nVrátiť sa dá hneď cez \u201eSpäť\u201c.')) return
    setDeleting(true)
    const snapshot = { employee_id: rec.employee_id, project_id: rec.project_id, project_name: rec.project_name, status: rec.status, notes: rec.notes, start_date: rec.start_date, end_date: rec.end_date }
    try {
      const r = await fetchAuth(`/api/work-schedule/${rec.id}`, { method: 'DELETE' })
      if (!r.ok) { toast.error('Mazanie zlyhalo'); setDeleting(false); return }
      if (toast.undo) {
        toast.undo('\uD83D\uDDD1 Zápis zmazaný \u00b7 Späť', async () => {
          await fetchAuth('/api/work-schedule/' + rec.id + '/restore', { method: 'PATCH' }).catch(() => {})   // soft-delete -> un-archive (Mirko 2026-06-04)
          onSaved()
        })
      } else { toast.error('Zápis zmazaný') }
      onSaved()
    } catch (e) { toast.error(`Chyba: ${e.message}`); setDeleting(false) }
  }

  const busy = ed.submitting || deleting
  const save = async () => {
    const err = validate ? validate(ed.values) : null     // vracia text chyby alebo null
    if (err) { toast.warn(err); return }
    // víkend a sviatok sa nerobí — oreže sa aj pri ručnej úprave (Mirko 6. 10. 2026)
    if (ed.values.status !== 'vacation' && ed.values.start_date && !(povolVikend && povolVikend(ed.values.employee_id))) {
      const r = naPracovneDni(ed.values.start_date, ed.values.end_date)
      if (!r) { toast.warn('V tom rozsahu nie je ani jeden pracovný deň'); return }
      if (r.zmenene) { ed.set('start_date', r.od); ed.set('end_date', r.do); toast.info('Upravené na pracovné dni: ' + r.od + ' – ' + r.do) }
    }
    await handleConfirm()
  }

  const footer = ed.phase === 'form' ? (
    <>
      {isEdit && <BtnDanger onClick={doDelete} disabled={busy || readOnly}>Zmazať</BtnDanger>}
      <span style={{ flex: 1 }} />
      <BtnGhost onClick={doClose}>Zrušiť</BtnGhost>
      {/* ULOŽENIE NA JEDNO KLIKNUTIE (Mirko 2.–3. 10. 2026: „zmenil som Jazdiareň Ribana na ukončené
          a nefunguje to"). Karta mala dva kroky — „Pokračovať →" viedlo na potvrdenie a kto tam
          neklikol druhýkrát, prišiel o zmenu. Rozpis je o rýchlej práci; istotu dáva Späť v lište. */}
      <BtnNav onClick={save} disabled={readOnly || busy}>{busy ? 'Ukladám…' : 'Uložiť'}</BtnNav>
    </>
  ) : (
    <>
      <BtnGhost onClick={ed.back}>← Späť</BtnGhost>
      <BtnAdd onClick={handleConfirm} loading={ed.submitting} disabled={busy}>
        {ed.submitting ? 'Ukladám…' : '✓ Potvrdiť uloženie'}
      </BtnAdd>
    </>
  )

  return (
    <>
      <Modal open onClose={doClose} onDirtyClose={doClose} isDirty={ed.isDirty}
        title={isEdit ? `Upraviť zápis — ${empName}` : `Nový zápis — ${empName}`}
        subtitle={ed.phase === 'form' ? 'Zákazka, obdobie, stav' : 'Skontroluj — pred potvrdením sa NIČ neuloží'}
        footer={footer}
      >
        {/* LOCK banner — zápis upravuje niekto iný (ústava 53.7) */}
        {readOnly && ed.lockedBy && (
          <div style={{
            background: TINT.remove, border: `1px solid ${COLORS.remove}`, borderRadius: RADIUS.md,
            padding: SPACE.sm, marginBottom: SPACE.md, fontSize: TYPO.sizes.sm, color: COLORS.remove
          }}>
            Zápis práve upravuje <strong>{ed.lockedBy}</strong>. Editácia je zamknutá.
          </div>
        )}
        {ed.error && (
          <div style={{
            background: TINT.remove, border: `1px solid ${COLORS.remove}`, borderRadius: RADIUS.md,
            padding: SPACE.sm, marginBottom: SPACE.md, fontSize: TYPO.sizes.sm, color: COLORS.remove
          }}>{ed.error}</div>
        )}

        {ed.phase === 'form' ? (
          <div style={readOnly ? { pointerEvents: 'none', opacity: 0.55 } : undefined}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
              {/* Predák — meniteľný (presun zápisu na iného predáka) */}
              <div>
                <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4 }}>
                  Predák / partia <span style={{ color: COLORS.remove }}>*</span>
                </label>
                <SearchableSelect
                  value={v.employee_id}
                  onChange={val => ed.set('employee_id', val)}
                  options={[...employees]
                    .filter(e => (e.is_partak || e.role === 'predák') && e.active !== 0 && e.active !== '0')
                    .sort((a,b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`,'sk'))
                    .map(e => ({ value: e.id, label: `${e.firstName} ${e.lastName}` }))}
                  placeholder="Hľadať predáka…"
                />
              </div>

              {/* Zákazka — searchable combobox (ústava 56.11, NIE plain select) */}
              <div>
                <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4 }}>
                  Zákazka {v.status !== 'vacation' && <span style={{ color: COLORS.remove }}>*</span>}
                  {v.status === 'vacation' && <span style={{ color: COLORS.muted, fontWeight: 400 }}> — pri Dovolenke nepovinná</span>}
                </label>
                <SearchableSelect
                  value={v.project_id}
                  onChange={val => ed.set('project_id', val)}
                  options={[...projects].sort((a,b) => (a.name||'').localeCompare(b.name||'')).map(p => ({ value: p.id, label: `${p.name} (${p.id})` }))}
                  emptyLabel="— budúca zákazka (zadaj názov nižšie) —"
                  placeholder="Hľadať zákazku (názov / číslo)…"
                />
              </div>

              {/* Budúca zákazka — len ak project_id prázdne */}
              {!v.project_id && (
                <div style={{ padding: SPACE.sm, background: COLORS.totalBg, border: `1px solid ${COLORS.total}`, borderRadius: RADIUS.md }}>
                  <Input label="Názov budúcej zákazky" value={v.project_name}
                    onChange={val => ed.set('project_name', val)}
                    placeholder="napr. NOVÁK Bratislava" fullWidth
                    hint="Zákazka ešte neexistuje — zápis bude Nespárovaný (oranžový), kým ju nevytvoríš" />
                  <div style={{ marginTop: SPACE.sm }}>
                    <BtnNav size="sm" onClick={() => onCreateProject(v.project_name)}>
                      + Vytvoriť zákazku teraz
                    </BtnNav>
                    <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginLeft: SPACE.sm }}>
                      (otvorí kartu Nová zákazka — ústava 10.3)
                    </span>
                  </div>
                </div>
              )}

              {/* Obdobie — PRESNÉ dni (deň-granularita, žiadne nasilu zarovnanie na týždeň) Mirko 2026-06-03 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md }}>
                <Input label="Od *" type="date" value={v.start_date}
                  onChange={val => ed.setValues({ start_date: val, end_date: (v.end_date && v.end_date >= val) ? v.end_date : val })} />
                <Input label="Do *" type="date" value={v.end_date}
                  onChange={val => ed.set('end_date', (val && val >= v.start_date) ? val : v.start_date)} />
              </div>
              <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: -SPACE.xs }}>
                Zadaj presné dni — akcia môže trvať aj časť týždňa (napr. Po–Str).
              </div>

              {/* Stav */}
              <div>
                <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4 }}>Stav</label>
                <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' }}>
                  {/* rozpis_stav_v1 (8. 10. 2026): položky zoznamu majú `value`, nie `key` —
                      pôvodné `s.key` bolo vždy undefined, takže klik nenastavil nič a ani jedno
                      tlačidlo sa netvárilo ako vybrané. Mirko: „tlačidlá sú tam, no nefungujú." */}
                  {STATUS_OPTS.map(s => {
                    const sc = statusCfg(s.value)
                    const isWhite = !!sc.textDark           // Dovolenka = biela → viditeľný sivý variant
                    const accent = isWhite ? '#94a3b8' : sc.color
                    const sel = v.status === s.value
                    return (
                      <button key={s.value} type="button" onClick={() => ed.set('status', s.value)}
                        style={{
                          padding: '5px 14px', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold,
                          color: sel ? (isWhite ? COLORS.text : '#fff') : accent,
                          background: sel ? (isWhite ? '#e5e7eb' : sc.color) : 'transparent',
                          border: `1px solid ${accent}`, borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit'
                        }}>{s.label}</button>
                    )
                  })}
                </div>
              </div>

              {/* KONTAKT NA ZÁKAZNÍKA — Mirko GO 28.7.: „hlavne preniesť zákazník údaje,
                  aby sme s ním ďalej mohli komunikovať". Platí aj pre budúcu zákazku. */}
              <div style={{ padding: SPACE.sm, background: TINT.nav, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: SPACE.sm }}>
                  <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.nav, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    Kontakt na zákazníka
                  </div>
                  <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                    aby sa dalo volať a písať priamo z Rozpisu
                  </span>
                  {v.project_id && !v.kontakt_meno && (
                    <button type="button" onClick={() => {
                      const ci = custInfo ? custInfo(v.project_id) : null
                      if (ci) ed.setValues({ kontakt_meno: ci.name || '', kontakt_telefon: ci.phone || '', kontakt_email: ci.email || '' })
                    }} style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, border: `1px solid ${COLORS.nav}`, background: '#fff', color: COLORS.nav, borderRadius: RADIUS.sm, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Doplniť zo zákazky
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
                  {/* p97 ústava 131.10.A: JEDEN blok kontaktnej osoby — osoba z registra partnera zákazky */}
                  <KontaktnaOsoba customerId={((projects || []).find(p => p.id === v.project_id) || {}).customerId}
                    inp={{ width: '100%', padding: '6px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.base, boxSizing: 'border-box' }}
                    osoba={{ meno: v.kontakt_meno, telefon: v.kontakt_telefon, email: v.kontakt_email }}
                    onZmena={o => { ed.set('kontakt_meno', o.meno); ed.set('kontakt_telefon', o.telefon); ed.set('kontakt_email', o.email) }}
                    popisy={{ osoba: 'Zákazník / kontaktná osoba' }}
                    obal={(tx, pole, kl) => <label key={kl} style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}><span style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, color: COLORS.text }}>{tx}</span>{pole}</label>} />
                  <Input label="Adresa stavby" value={v.kontakt_adresa} onChange={val => ed.set('kontakt_adresa', val)} fullWidth />
                </div>
              </div>

              {/* POZNAMKA_ZAKAZKY — vazba je na zakazku, nie na riadok (Mirko 29.7.) */}
              <PoznamkaZakazky projectId={v.project_id} projects={projects} ed={ed} v={v} />

              {/* Riadkova poznamka ZRUSENA (Mirko 29.7.) — vsetko ide do poznamky zakazky vyssie. */}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
            <div style={{ padding: SPACE.md, background: COLORS.addBg, border: `1px solid ${COLORS.add}`, borderRadius: RADIUS.md, fontSize: TYPO.sizes.sm }}>
              <strong>{isEdit ? 'Zmeny sa uložia' : 'Nový zápis vznikne'}</strong> — žiadne dáta sa zatiaľ neuložili.
            </div>
            {isEdit ? (
              /* DIFF — len zmenené polia (ústava 53.8) */
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
                <thead>
                  <tr style={{ background: TINT.nav }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: COLORS.nav, width: 130 }}>Pole</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: COLORS.nav }}>Pôvodné</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: COLORS.nav }}>Nové</th>
                  </tr>
                </thead>
                <tbody>
                  {ed.diff.map(d => (
                    <tr key={d.name} style={{ borderBottom: `1px solid ${COLORS.lineSoft}` }}>
                      <td style={{ padding: '6px 8px', color: COLORS.muted }}>{d.label}</td>
                      <td style={{ padding: '6px 8px', color: COLORS.muted, textDecoration: 'line-through' }}>{d.from}</td>
                      <td style={{ padding: '6px 8px', color: COLORS.add, fontWeight: TYPO.weights.semibold }}>{d.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
                <tbody>
                  {[
                    ['Predák', empName],
                    ['Zákazka', v.project_id
                      ? `${(projects.find(p=>p.id===v.project_id)||{}).name||''} (${v.project_id})`
                      : `${v.project_name} — budúca (Nespárované)`],
                    ['Obdobie', `${v.start_date} → ${v.end_date}`],
                    ['Stav', statusCfg(v.status).label],
                    ['Zákazník', v.kontakt_meno || '—'],
                    ['Telefón', v.kontakt_telefon || '—'],
                    ['E-mail', v.kontakt_email || '—'],
                    ['Poznámka', v.notes || '—']
                  ].map(([k, val]) => (
                    <tr key={k} style={{ borderBottom: `1px solid ${COLORS.lineSoft}` }}>
                      <td style={{ padding: '6px 8px', color: COLORS.muted, width: 140 }}>{k}</td>
                      <td style={{ padding: '6px 8px', fontWeight: TYPO.weights.semibold }}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Modal>
      <Modal.DirtyConfirm {...ed.dirtyProps} />
    </>
  )
}

// ── Komunikácia so zákazníkom priamo z Rozpisu (Mirko 22.7.): klik na blok, vlákno zo spisu zákazky,
// zapísať hovor (Info / Posun termínu u nás / u zákazníka) + SMS s predlohami. Bez editácie zápisu. ──
